/* eslint-disable camelcase */
import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import { pageSelector } from 'selectors/page/pageSelector';
import { coreUserDataSelector } from 'viewModel/selectors/user/coreUserDataSelector';
import superChatActions from 'ai/actions/superChatActions';
import { superChatSelector } from 'ai/selectors/superChatSelector';
import sdnApi from 'ai/services/sdn';
import { getGENAIAnonymousId } from 'ai/utils/sessionStorage';
import PageTemplateType from 'constants/PageTemplateType';
import { ENTRYPOINTS } from 'ai/constants/superchat';
import Location from 'utils/Location';
import { isGenAINewUIEnabledWithRCPS, getSearchKeyword, getEcid } from 'ai/utils/aiBeautyChat';

const { wrapHOC } = FrameworkUtils;
const { getTextFromResource, getLocaleResourceFile } = LanguageLocaleUtils;
const getText = getLocaleResourceFile('ai/components/EntryPoint/locales', 'AIChat');
const getSuperChatText = getLocaleResourceFile('ai/components/SuperChat/locales', 'SuperChat');

const { entryPointPrompts } = sdnApi;

const localization = createStructuredSelector({
    new: getTextFromResource(getText, 'new'),
    aiChatInputPlaceholderPlp: getTextFromResource(getText, 'aiChatInputPlaceholderPlp'),
    aiChatInputPlaceholderPlpLgUi: getTextFromResource(getText, 'aiChatInputPlaceholderPlpLgUi'),
    lookingForAGift: getTextFromResource(getSuperChatText, 'lookingForAGift')
});

const functions = (dispatch, _ownProps) => ({
    fetchEntryPointPromptsPlp: async (nthCategory, searchKeyword, clientId, anonymousId) => {
        const categoryId = nthCategory?.categoryId || '';
        const isSearchPage = Sephora.template === PageTemplateType.Search;
        const page = isSearchPage ? ENTRYPOINTS.SRP : ENTRYPOINTS.PLP;
        const searchTerm = isSearchPage ? searchKeyword : '';
        const entityName = isSearchPage ? '' : nthCategory?.displayName || '';
        const payload = {
            categoryId,
            searchTerm,
            entityName,
            page,
            clientId,
            anonymousId,
            ecid: getEcid()
        };

        return entryPointPrompts(payload).then(data => data);
    },
    handlePromptClick: (prompts, nthCategory, products, selectedPrompt = null, searchKeyword = null) => {
        const categoryId = nthCategory?.categoryId || '';
        const isSearchPage = Location.isSearchPage();
        const page = isSearchPage ? ENTRYPOINTS.SRP : ENTRYPOINTS.PLP;

        const data = {
            entrypoint: page,
            category_id: categoryId,
            products: products
                ?.filter(product => product.currentSku)
                ?.slice(0, 30)
                .map(product => ({
                    product_id: product.productId,
                    sku_id: product.currentSku.skuId
                })),
            selectedPrompt,
            source: isSearchPage ? 'srp-entry' : 'plp-entry'
        };

        if (isSearchPage) {
            data.search_term = searchKeyword;
        }

        dispatch(superChatActions.showSuperChat(true, prompts, data));
    },
    updatePrompts: prompts => {
        dispatch(superChatActions.setContextPrompts(prompts));
    }
});

const fields = createSelector(
    localization,
    coreUserDataSelector,
    pageSelector,
    superChatSelector,
    (_state, ownProps) => ownProps.giftFinder,
    (locales, user, page, superChat, giftFinder) => {
        const clientId = user?.biId;
        let anonymousId = undefined;

        if (!Sephora.isNodeRender) {
            anonymousId = getGENAIAnonymousId();
        }

        let chatName = superChat?.config?.meta?.try_chat_name || '';

        if (giftFinder) {
            chatName = superChat?.config?.meta?.try_gift_chat_name || '';
        }

        const isGenAINewUIEnabled = isGenAINewUIEnabledWithRCPS();

        return {
            localization: locales,
            products: page?.nthCategory?.skuList || [],
            chatName: chatName,
            searchKeyword: getSearchKeyword(page),
            nthCategory: page?.nthCategory || {},
            clientId,
            anonymousId,
            isRequestInFlight: superChat?.isRequestInFlight || false,
            isGenAINewUIEnabled
        };
    }
);

const withProductListPageProps = wrapHOC(connect(fields, functions));

export {
    withProductListPageProps, fields, functions
};
